/**
 * Onde está a cláusula de autorização do desconto, na revisão do ponto (E168.F5, AJ-03; E172.F6,
 * GUS-230 B1.2, ADR-0124 §13).
 *
 * Com a proposta do sistema, os campos já vêm com ela: o assistente técnico confere no documento e
 * confirma sem colar. Pode trocar por outra passagem localizada ou corrigir à mão. Sem proposta, a
 * tela diz por quê (não localizada, CCB sem texto) e a indicação é manual. Sai o limite de 15
 * palavras: o trecho literal vai até o teto de segurança do servidor.
 */
import { Button, Field, Input, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  TETO_DO_DOCUMENTO,
  TETO_DO_TRECHO,
  type ClausulaPeloMotorData,
  type ClausulaPropostaData,
  type LocalizacaoDaClausulaData,
} from '@/lib/formalizacaoAvaliacao';

const tl = ptBR.formalizacaoAvaliacao.formulario.localizacao;

export interface LocalDaClausula {
  documento: string;
  pagina: string;
  clausula: string;
  trecho: string;
}

export const LOCAL_VAZIO: LocalDaClausula = { documento: '', pagina: '', clausula: '', trecho: '' };

/** Os campos com uma passagem localizada pelo sistema. */
export function localDaPassagem(p: ClausulaPropostaData): LocalDaClausula {
  return {
    documento: p.documento,
    pagina: String(p.pagina),
    clausula: p.clausula ?? '',
    trecho: p.trecho,
  };
}

/** O ponto de partida dos campos: a proposta, quando houve. */
export function localInicial(clausula: ClausulaPeloMotorData | null | undefined): LocalDaClausula {
  return clausula?.proposta ? localDaPassagem(clausula.proposta) : LOCAL_VAZIO;
}

/** A localização que sai, ou `null` com algum campo em falta. */
export function localizacaoDe(l: LocalDaClausula): LocalizacaoDaClausulaData | null {
  const pagina = Number(l.pagina);
  const trecho = l.trecho.trim();
  if (!l.documento.trim() || !Number.isInteger(pagina) || pagina < 1 || !trecho) return null;
  return { documento: l.documento.trim(), pagina, clausula: l.clausula.trim() || null, trecho };
}

function Passagem({
  p,
  onUsar,
}: {
  p: ClausulaPropostaData;
  onUsar: (p: ClausulaPropostaData) => void;
}): JSX.Element {
  const numerada = p.clausula ? `${p.clausula} · ` : '';
  return (
    <li className="space-y-1" data-testid="passagem-da-clausula">
      <p className="text-caption text-fg-muted">
        {numerada}
        {p.documento}, pág. {p.pagina} ({tl.proposta.criterio[p.criterio]})
      </p>
      <blockquote className="border-l-2 border-border pl-sc-2 text-body-sm">{p.trecho}</blockquote>
      <Button type="button" variant="secondary" size="sm" onClick={() => onUsar(p)}>
        {tl.proposta.usar}
      </Button>
    </li>
  );
}

function DoSistema({
  clausula,
  onUsar,
}: {
  clausula: ClausulaPeloMotorData;
  onUsar: (p: ClausulaPropostaData) => void;
}): JSX.Element {
  if (clausula.proposta === null) {
    const aviso =
      clausula.situacao === 'nao_lida'
        ? tl.situacao.nao_lida.replace('{nomes}', clausula.nao_lidas.join(', '))
        : clausula.situacao === 'sem_ccb'
          ? tl.situacao.sem_ccb
          : clausula.situacao === 'excede_o_teto'
            ? tl.situacao.excede_o_teto.replace('{onde}', (clausula.excedentes ?? []).join('; '))
            : clausula.situacao === 'outra_finalidade'
              ? tl.situacao.outra_finalidade.replace(
                  '{onde}',
                  (clausula.outra_finalidade ?? []).join('; '),
                )
              : tl.situacao.nao_localizada;
    return (
      <p className="text-body-sm text-warning-700" data-testid="clausula-sem-proposta">
        {aviso}
      </p>
    );
  }
  return (
    <div className="space-y-sc-2" data-testid="clausula-proposta">
      <p className="text-caption text-fg-dim">{tl.proposta.titulo}</p>
      <p className="text-caption text-fg-muted">{tl.proposta.dica}</p>
      <ul className="space-y-sc-2">
        <Passagem p={clausula.proposta} onUsar={onUsar} />
      </ul>
      {clausula.outras.length > 0 ? (
        <details>
          <summary className="cursor-pointer text-caption text-fg-dim">
            {tl.proposta.outras}
          </summary>
          <ul className="mt-sc-2 space-y-sc-2">
            {clausula.outras.map((o, i) => (
              <Passagem key={`${o.documento_id}-${o.pagina}-${i}`} p={o} onUsar={onUsar} />
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

export interface ClausulaDaAutorizacaoProps {
  local: LocalDaClausula;
  tentou: boolean;
  clausula?: ClausulaPeloMotorData | null | undefined;
  onMudar: (local: LocalDaClausula) => void;
}

export function ClausulaDaAutorizacao({
  local,
  tentou,
  clausula,
  onMudar,
}: ClausulaDaAutorizacaoProps): JSX.Element {
  const paginaValida = Number.isInteger(Number(local.pagina)) && Number(local.pagina) >= 1;
  function campo(nome: keyof LocalDaClausula) {
    return (e: { target: { value: string } }) => onMudar({ ...local, [nome]: e.target.value });
  }
  return (
    <fieldset className="space-y-sc-2" data-testid="localizacao-da-clausula">
      <legend className="mb-[5px] text-caption text-fg-dim">{tl.titulo}</legend>
      {clausula ? (
        <DoSistema clausula={clausula} onUsar={(p) => onMudar(localDaPassagem(p))} />
      ) : null}
      <Field
        label={tl.documento}
        description={tl.documentoDica}
        error={tentou && !local.documento.trim() ? tl.faltaDocumento : undefined}
        required
      >
        <Input
          value={local.documento}
          maxLength={TETO_DO_DOCUMENTO}
          data-campo="documento"
          onChange={campo('documento')}
        />
      </Field>
      <div className="grid grid-cols-1 gap-sc-2 sm:grid-cols-2">
        <Field
          label={tl.pagina}
          error={tentou && !paginaValida ? tl.faltaPagina : undefined}
          required
        >
          <Input
            value={local.pagina}
            inputMode="numeric"
            maxLength={5}
            data-campo="pagina"
            onChange={campo('pagina')}
          />
        </Field>
        <Field label={tl.clausula} description={tl.clausulaDica}>
          <Input
            value={local.clausula}
            maxLength={60}
            data-campo="clausula"
            onChange={campo('clausula')}
          />
        </Field>
      </div>
      <Field
        label={tl.trecho}
        description={tl.trechoDica}
        error={tentou && !local.trecho.trim() ? tl.faltaTrecho : undefined}
        required
      >
        <Textarea
          value={local.trecho}
          maxLength={TETO_DO_TRECHO}
          rows={4}
          data-campo="trecho"
          onChange={campo('trecho')}
        />
      </Field>
    </fieldset>
  );
}
